import React, { useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { GraduationCap, Users, UserCircle, ShieldCheck, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { authApi } from '../../api/examApi';
import { toast } from 'sonner';
import { parseApiError } from '../../lib/apiErrors';
import { consumeAuthNotice } from '../../lib/authNotice';
import LOGO_URL from '../../assets/logo.jpeg';

// The admin portal's URL segment is a private slug, not the role name. It is
// obscurity layered on top of real auth (backend login + rate-limiting), never
// a substitute for it. Exported so the one in-app reference (Sidebar's
// post-logout redirect) can't drift from the route.
export const ADMIN_LOGIN_SLUG = 'PitajiDrRam';
export const ADMIN_LOGIN_PATH = `/login/${ADMIN_LOGIN_SLUG}`;

// URL segment -> portal role. Anything not listed here is an unknown role.
const ROLE_BY_SLUG = {
  student: 'student',
  faculty: 'faculty',
  [ADMIN_LOGIN_SLUG]: 'admin',
};

// Role-select hub at /login. Student + Faculty only — the admin portal is
// intentionally unlinked and reachable by direct URL entry only.
export function RoleSelectPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [notice] = useState(() => consumeAuthNotice());

  // Carry ?next= through to the portal so ProtectedRoute's deep-link
  // redirect still resolves after sign-in.
  const goToPortal = (role) => navigate(`/login/${role}${location.search}`);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="Future Makers" className="h-20 mx-auto mb-4 rounded-lg bg-white p-2" />
          <h1 className="text-3xl font-heading font-bold text-slate-900 dark:text-white">
            Welcome to Future Makers
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-2">
            Educational Institute Management System
          </p>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6 shadow-sm">
          {notice && (
            <p role="alert" data-testid="auth-notice" className="mb-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-3 py-2 text-sm text-amber-800 dark:text-amber-200">
              {notice}
            </p>
          )}
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-6 text-center">
            Select your role to continue
          </h2>

          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => goToPortal('student')}
              className="flex flex-col items-center gap-3 p-6 rounded-lg border-2 border-slate-200 dark:border-slate-700 hover:border-primary dark:hover:border-accent-cool hover:bg-primary/5 dark:hover:bg-accent-cool/5 transition-all"
              data-testid="select-student"
            >
              <div className="p-4 bg-primary/10 dark:bg-accent-cool/10 rounded-full">
                <GraduationCap className="w-8 h-8 text-primary dark:text-accent-cool" />
              </div>
              <span className="font-medium text-slate-900 dark:text-white">Student</span>
              <span className="text-xs text-slate-500 dark:text-slate-400">Take exams</span>
            </button>

            <button
              onClick={() => goToPortal('faculty')}
              className="flex flex-col items-center gap-3 p-6 rounded-lg border-2 border-slate-200 dark:border-slate-700 hover:border-secondary hover:bg-secondary/5 transition-all"
              data-testid="select-faculty"
            >
              <div className="p-4 bg-secondary/10 rounded-full">
                <Users className="w-8 h-8 text-secondary" />
              </div>
              <span className="font-medium text-slate-900 dark:text-white">Faculty</span>
              <span className="text-xs text-slate-500 dark:text-slate-400">Manage exams</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function LoginPage() {
  const { role } = useParams();
  const userType = ROLE_BY_SLUG[role] || null;
  const [isRegister, setIsRegister] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [registered, setRegistered] = useState(null);
  const [notice] = useState(() => consumeAuthNotice());
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
  });

  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const nextPath = useMemo(() => {
    const next = new URLSearchParams(location.search).get('next');
    return next || null;
  }, [location.search]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setFieldErrors(prev => (prev[name] ? { ...prev, [name]: undefined } : prev));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setFieldErrors({});
    setFormError('');

    try {
      let response;
      if (userType === 'student') {
        if (isRegister) {
          const reg = await authApi.studentRegister(formData);
          // Registration never authenticates; show the pending screen instead.
          setRegistered(reg.data.message);
          setFormData({ name: '', email: '', password: '' });
          return;
        } else {
          response = await authApi.studentLogin({
            email: formData.email,
            password: formData.password,
          });
        }
      } else if (userType === 'faculty') {
        response = await authApi.facultyLogin({
          email: formData.email,
          password: formData.password,
        });
      } else {
        response = await authApi.adminLogin({
          email: formData.email,
          password: formData.password,
        });
      }

      const { access_token, user_type, user_id, name, email } = response.data;
      login({ user_type, user_id, name, email }, access_token);
      
      toast.success(`Welcome, ${name}!`);
      
      if (user_type === 'student' && nextPath) {
        navigate(nextPath);
      } else if (user_type === 'student') {
        navigate('/student');
      } else if (user_type === 'faculty') {
        navigate('/faculty');
      } else {
        navigate('/admin');
      }
    } catch (err) {
      // Display only: a 422 detail is an array of per-field errors, never a string.
      const { fieldErrors: fe, message } = parseApiError(err, 'Login failed');
      setFieldErrors(fe);
      // Non-field failures (wrong password, awaiting approval, lockout...) stay
      // on screen as text; per-field 422s render under their inputs.
      if (Object.keys(fe).length === 0) setFormError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Unknown role in the URL falls back to the hub rather than rendering a
  // portal for a role that doesn't exist.
  if (!userType) {
    return <Navigate to="/login" replace />;
  }

  if (registered) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6 shadow-sm text-center" data-testid="awaiting-approval">
          <h1 className="text-xl font-heading font-bold text-slate-900 dark:text-white mb-2">Awaiting approval</h1>
          <p className="text-slate-600 dark:text-slate-300 mb-6">{registered}</p>
          <button
            onClick={() => { setRegistered(null); setIsRegister(false); }}
            className="w-full py-2.5 rounded-lg font-medium text-white bg-primary hover:bg-primary-light"
            data-testid="back-to-signin"
          >
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="Future Makers" className="h-16 mx-auto mb-4 rounded-lg bg-white p-2" />
          <h1 className="text-2xl font-heading font-bold text-slate-900 dark:text-white">
            {userType === 'student' ? 'Student Portal' : userType === 'faculty' ? 'Faculty Portal' : 'Admin Portal'}
          </h1>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6 shadow-sm">
          <div className="flex items-center justify-center gap-2 mb-6">
            <div className={`p-2 rounded-full ${userType === 'student' ? 'bg-primary/10' : userType === 'faculty' ? 'bg-secondary/10' : 'bg-emerald-500/10'}`}>
              {userType === 'student' ? (
                <GraduationCap className="w-5 h-5 text-primary dark:text-accent-cool" />
              ) : userType === 'faculty' ? (
                <UserCircle className="w-5 h-5 text-secondary" />
              ) : (
                <ShieldCheck className="w-5 h-5 text-emerald-500" />
              )}
            </div>
            <span className="font-medium text-slate-900 dark:text-white capitalize">
              {isRegister ? 'Create Account' : 'Sign In'}
            </span>
          </div>

          {notice && (
            <p role="alert" data-testid="auth-notice" className="mb-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-3 py-2 text-sm text-amber-800 dark:text-amber-200">
              {notice}
            </p>
          )}
          {formError && (
            <p role="alert" data-testid="login-error" className="mb-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-3 py-2 text-sm text-red-700 dark:text-red-300">
              {formError}
            </p>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            {isRegister && userType === 'student' && (
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                  Full Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary dark:focus:ring-accent-cool focus:border-transparent"
                  placeholder="Enter your name"
                  data-testid="input-name"
                />
                <p role="alert" data-testid="error-name" className="mt-1 text-sm text-red-600 dark:text-red-400">{fieldErrors.name}</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                Email Address
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-3 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary dark:focus:ring-accent-cool focus:border-transparent"
                placeholder="Enter your email"
                data-testid="input-email"
              />
              <p role="alert" data-testid="error-email" className="mt-1 text-sm text-red-600 dark:text-red-400">{fieldErrors.email}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  minLength={6}
                  className="w-full px-3 py-2.5 pr-10 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary dark:focus:ring-accent-cool focus:border-transparent"
                  placeholder="Enter password"
                  data-testid="input-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <p role="alert" data-testid="error-password" className="mt-1 text-sm text-red-600 dark:text-red-400">{fieldErrors.password}</p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full py-2.5 rounded-lg font-medium text-white transition-colors disabled:opacity-50 ${
                userType === 'student' 
                  ? 'bg-primary hover:bg-primary-light' 
                  : 'bg-secondary hover:bg-secondary-light'
              }`}
              data-testid="submit-login"
            >
              {loading ? 'Please wait...' : (isRegister ? 'Create Account' : 'Sign In')}
            </button>
          </form>

          {userType === 'student' && (
            <p className="text-center text-sm text-slate-500 dark:text-slate-400 mt-4">
              {isRegister ? 'Already have an account?' : "Don't have an account?"}{' '}
              <button
                onClick={() => setIsRegister(!isRegister)}
                className="text-primary dark:text-accent-cool hover:underline font-medium"
                data-testid="toggle-register"
              >
                {isRegister ? 'Sign In' : 'Register'}
              </button>
            </p>
          )}

          <button
            onClick={() => navigate(`/login${location.search}`)}
            className="w-full mt-4 py-2 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
            data-testid="back-to-role"
          >
            ← Back to role selection
          </button>
        </div>
      </div>
    </div>
  );
}
